/**
 * Import React libraries.
 */
import * as React from 'react';


/**
 * Import custom styles.
 */
import {COLORS, ComponentSStyling, focusStyles} from '../styles';
import {NoteMessage, readOnlyState, ValidationMessage} from './styling';
import { States } from '../types';

/**
 * Import custom components.
 */
import { SystemIcons } from '../icons';

/**
 * Import third-party libraries.
 */
import styled from 'styled-components';
import { ComponentMStyling, ComponentTextStyle } from '../styles';
import { TextareaProps, TextFieldNote } from './types';
import {useFocusVisibleRef} from "../common";

// Add component-specific styles.
const TextareaWrapper = styled.div<{ margin?: string }>`
  width: 100%;
  ${(props) => (props.margin ? `margin: ${props.margin};` : '')}
`;
const TextArea = styled.textarea<{ autoHeight?: boolean }>`
  display: block;
  background: ${COLORS.white};
  border-radius: 4px;
  outline: none;
  margin-bottom: 4px;
  padding: 16px;
  border: none;
  box-shadow: inset 0 0 0 1px ${COLORS.neutral_400};

  resize: ${(props) => (props.autoHeight ? 'none' : '')};

  ${ComponentMStyling(ComponentTextStyle.Regular, COLORS.black)}
  &.small {
    ${ComponentSStyling(ComponentTextStyle.Regular, COLORS.black)}
    width: 304px;

    &::placeholder {
      ${ComponentSStyling(ComponentTextStyle.Italic, COLORS.neutral_500)}
    }
  }

  &.medium {
    ${ComponentMStyling(ComponentTextStyle.Regular, COLORS.black)}
    &::placeholder {
      ${ComponentMStyling(ComponentTextStyle.Italic, COLORS.neutral_500)}
    }
    width: 344px;
  }

  &:focus:not(.focus-visible) {
    box-shadow: inset 0 0 0 2px ${COLORS.primary_800};
  }

  &.focus-visible {
    ${focusStyles}
  }

  &.invalid {
    box-shadow: inset 0 0 0 2px ${COLORS.critical_500};
  }

  &.valid {
    box-shadow: inset 0 0 0 2px ${COLORS.correct_500};
  }

  &:hover {
    box-shadow: inset 0 0 0 2px ${COLORS.primary_700};
  }

  &.active {
    box-shadow: inset 0 0 0 2px ${COLORS.primary_800};
  }

  &:read-only:not(:disabled) {
    ${readOnlyState}
  }

  &:disabled {
    cursor: not-allowed;
    border: 1px solid ${COLORS.neutral_100};
    box-shadow: none !important;
    color: ${COLORS.neutral_300} !important;

    &::placeholder {
      color: ${COLORS.neutral_300};
    }
  }

  &::placeholder {
    ${ComponentMStyling(ComponentTextStyle.Italic, COLORS.neutral_500)}
  }
`;

const Textarea = React.forwardRef<any, TextareaProps>(({
  id,
  placeholder,
  state,
  value,
  size,
  disabled,
  validationMessage,
  onChange,
  margin,
  autoHeight,
  note,
  required,
  className,
  dataTestId,
  ...rest
}: TextareaProps, ref) => {

  const elementRef = useFocusVisibleRef();

  React.useImperativeHandle(ref, () => elementRef.current, [elementRef]);

  const cls = `${size} ${state || (validationMessage ? States.Invalid : '')} ${className || ''}`;
  return (
    <TextareaWrapper>
      <TextArea
        id={id}
        data-testid={dataTestId}
        ref={elementRef}
        value={value}
        placeholder={disabled ? undefined : placeholder}
        autoHeight={autoHeight}
        className={cls}
        disabled={disabled}
        onChange={(e: any) => onChange && onChange(e?.target?.value || '')}
        required={required}
        {...rest}
      />
      {validationMessage && (
        <ValidationMessage className={size || ''} type={state ?? States.Invalid}>
          {state === States.Valid ? <SystemIcons.CheckMark color={COLORS.correct_400} /> : <SystemIcons.TechnicalWarning color={COLORS.critical_400} />}
          <span>{validationMessage}</span>
        </ValidationMessage>
      )}
      {note && !disabled && (
        <NoteMessage className={size}>
          {note.icon}
          <span>{note.message}</span>
        </NoteMessage>
      )}
    </TextareaWrapper>
  );
});

export default Textarea;
